import type { CatalogItem } from '@viva/core';
import { useEffect, useState } from 'react';

/**
 * Where the viewer stopped in movies and episodes, so Movies and Series can
 * offer "Continue watching" and the player can resume. Kept on this device.
 */
export interface WatchEntry {
  sourceId: string;
  /** The movie, or the episode last watched. */
  item: CatalogItem;
  /** Episodes: the series they belong to (its poster and episode list). */
  series?: CatalogItem;
  /** Seconds. */
  position: number;
  duration: number;
  updatedAt: number;
}

const STORAGE_KEY = 'viva.watching';
const MAX_ENTRIES = 40;
/** Less than this watched doesn't count as started. */
export const MIN_STARTED_S = 30;
/** Watched to here (or this close to the end) counts as finished. */
const FINISHED_SHARE = 0.95;
const FINISHED_LEFT_S = 90;

type Listener = () => void;
const listeners = new Set<Listener>();

/** One entry per movie and per series: a series keeps only its latest episode. */
function entryId(entry: Pick<WatchEntry, 'sourceId' | 'item' | 'series'>): string {
  return `${entry.sourceId}\u0000${entry.series?.key ?? entry.item.key}`;
}

export function isFinished(position: number, duration: number): boolean {
  if (!(duration > 0)) return false;
  return position >= duration * FINISHED_SHARE || duration - position <= FINISHED_LEFT_S;
}

/** Entries newest first. Storage can be missing or broken (private mode, TV quirks): then nothing. */
export function readWatching(storage: Storage | undefined = safeStorage()): WatchEntry[] {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return (parsed as WatchEntry[])
      .filter((entry) => entry && typeof entry.sourceId === 'string' && entry.item && typeof entry.position === 'number')
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

/**
 * Records progress. Under `MIN_STARTED_S` changes nothing; a finished movie
 * or episode leaves the list.
 */
export function recordProgress(entry: WatchEntry, storage: Storage | undefined = safeStorage()): void {
  if (!storage || entry.item.kind === 'live') return;
  if (entry.position < MIN_STARTED_S && !isFinished(entry.position, entry.duration)) return;
  const id = entryId(entry);
  const others = readWatching(storage).filter((existing) => entryId(existing) !== id);
  const next = isFinished(entry.position, entry.duration) ? others : [compact(entry), ...others].slice(0, MAX_ENTRIES);
  write(storage, next);
}

/** Where to resume this movie or episode, or null to start from the beginning. */
export function resumePosition(sourceId: string, itemKey: string, storage: Storage | undefined = safeStorage()): number | null {
  const entry = readWatching(storage).find((candidate) => candidate.sourceId === sourceId && candidate.item.key === itemKey);
  if (!entry || entry.position < MIN_STARTED_S || isFinished(entry.position, entry.duration)) return null;
  return entry.position;
}

export function forgetWatching(sourceId: string, item: CatalogItem, storage: Storage | undefined = safeStorage()): void {
  if (!storage) return;
  write(
    storage,
    readWatching(storage).filter((entry) => !(entry.sourceId === sourceId && (entry.item.key === item.key || entry.series?.key === item.key))),
  );
}

/** "Continue watching" for one source and kind, newest first; updates when progress is saved. */
export function useWatching(sourceId: string, kind: 'movie' | 'series'): WatchEntry[] {
  const [entries, setEntries] = useState(() => watchingFor(sourceId, kind));
  useEffect(() => {
    const update = () => setEntries(watchingFor(sourceId, kind));
    update();
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, [sourceId, kind]);
  return entries;
}

export function watchingFor(sourceId: string, kind: 'movie' | 'series', storage: Storage | undefined = safeStorage()): WatchEntry[] {
  return readWatching(storage).filter((entry) => entry.sourceId === sourceId && (kind === 'series' ? !!entry.series : !entry.series));
}

/** Long plots and channel variants aren't needed to show a card or play it again. */
function compact(entry: WatchEntry): WatchEntry {
  const slim = (item: CatalogItem): CatalogItem => {
    const { plot: _plot, variants: _variants, ...data } = item.data;
    return { ...item, data };
  };
  return { ...entry, item: slim(entry.item), series: entry.series ? slim(entry.series) : undefined };
}

function write(storage: Storage, entries: WatchEntry[]): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Full or blocked storage: only "Continue watching" is lost.
  }
  listeners.forEach((listener) => listener());
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
